import React, { useState } from 'react';
import { X, Plus, Printer, RotateCcw, CreditCard } from 'lucide-react';
import { toast } from 'react-toastify';
import AddPaymentModal from './AddPaymentModal';
import invoiceGenerator from '../../services/invoiceGenerator';
import apiService from '../../services/api';

const ViewPaymentsModal = ({ sale, transaction, onClose, onPaymentDeleted }) => {
    const t = transaction || sale || {};
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
    const [payments, setPayments] = useState(t.payment_lines || []);
    const [actionLoading, setActionLoading] = useState(false);

    const invoiceNo = t.invoice_no || t.ref_no || '--';
    const txDate = t.transaction_date || t.created_at || '--';
    const supplierName = t.contact?.name || t.supplier || '--';
    const locationName = t.location?.name || '--';
    const paymentStatus = t.payment_status || '--';
    const finalTotal = parseFloat(t.final_total || 0);
    const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const paymentDue = t.payment_due ?? Math.max(0, finalTotal - totalPaid);

    const formatDate = (d) => {
        if (!d || d.startsWith('0000')) return '--';
        const parsed = new Date(d);
        return isNaN(parsed.getTime()) ? d : parsed.toLocaleString();
    };

    const statusBadge = (status) => {
        const colors = { paid: '#16a34a', partial: '#d97706', due: '#dc2626', overdue: '#dc2626' };
        return (
            <span style={{ background: colors[status] || 'var(--etpos-text-muted, #6b7280)', color: 'var(--etpos-card)', borderRadius: '4px', padding: '0.15rem 0.55rem', fontSize: '0.75rem', fontWeight: 700, textTransform: 'capitalize' }}>
                {status}
            </span>
        );
    };

    const handlePaymentAdded = () => {
        setShowAddPaymentModal(false);
        if (onPaymentDeleted) onPaymentDeleted();
        onClose();
    };

    const handleApplyCheckReturn = async (paymentId) => {
        if (!window.confirm('Are you sure you want to apply check return charges?')) return;
        try {
            setActionLoading(true);
            const res = await apiService.applyCheckReturn(paymentId, t.id);
            if (res.success || res.status === 'success' || res.msg || res.message) {
                toast.success(res.msg || res.message || 'Check return charges applied successfully');
                if (onPaymentDeleted) onPaymentDeleted();
                onClose();
            } else {
                toast.error(res.message || 'Failed to apply check return');
            }
        } catch (err) {
            toast.error(err.message || 'Error applying check return');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevertCheckReturn = async (paymentId) => {
        if (!window.confirm('Are you sure you want to revert check return?')) return;
        try {
            setActionLoading(true);
            const res = await apiService.revertCheckReturn(paymentId, t.id);
            if (res.success || res.status === 'success' || res.msg || res.message) {
                toast.success(res.msg || res.message || 'Check return reverted successfully');
                if (onPaymentDeleted) onPaymentDeleted();
                onClose();
            } else {
                toast.error(res.message || 'Failed to revert check return');
            }
        } catch (err) {
            toast.error(err.message || 'Error reverting check return');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '2rem' }}>
            <div style={{ background: 'var(--etpos-card)', width: '100%', maxWidth: '1150px', borderRadius: '4px', display: 'flex', flexDirection: 'column', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', maxHeight: '90vh' }}>

                {/* Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #4b5563)', fontWeight: '500' }}>
                        View Payments ( Invoice No.: {invoiceNo} )
                    </h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', padding: '0.2rem' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem', overflowY: 'auto' }}>

                    {/* Info Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text, #4b5563)', lineHeight: '1.6' }}>
                            <div style={{ fontWeight: 700, color: 'var(--etpos-text, #111827)', marginBottom: '0.25rem' }}>Supplier / Customer:</div>
                            <div>{supplierName}</div>
                            <div>{locationName}</div>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text, #4b5563)', lineHeight: '1.6' }}>
                            <div style={{ fontWeight: 700, color: 'var(--etpos-text, #111827)', marginBottom: '0.25rem' }}>Invoice Info:</div>
                            <div><strong>Invoice No.:</strong> {invoiceNo}</div>
                            <div><strong>Date:</strong> {formatDate(txDate)}</div>
                            <div><strong>Payment Status:</strong> {statusBadge(paymentStatus)}</div>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--etpos-text, #4b5563)', lineHeight: '1.6' }}>
                            <div style={{ fontWeight: 700, color: 'var(--etpos-text, #111827)', marginBottom: '0.25rem' }}>Summary:</div>
                            <div><strong>Total:</strong> ${finalTotal.toFixed(2)}</div>
                            <div><strong>Paid:</strong> <span style={{ color: '#16a34a' }}>${totalPaid.toFixed(2)}</span></div>
                            <div><strong>Due:</strong> <span style={{ color: paymentDue > 0 ? '#dc2626' : '#16a34a' }}>${parseFloat(paymentDue).toFixed(2)}</span></div>
                        </div>
                    </div>

                    {/* Add Payment Button */}
                    {parseFloat(paymentDue) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                            <button onClick={() => setShowAddPaymentModal(true)} style={{ background: 'var(--etpos-card)', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '4px', padding: '0.4rem 0.75rem', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                                <Plus size={16} /> Add Payment
                            </button>
                        </div>
                    )}

                    {/* Payments Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--etpos-bg, #f9fafb)', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                                {['#', 'Date', 'Reference No', 'Amount', 'Previous Hold Amount', 'Method', 'Note', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--etpos-text, #111827)', fontWeight: '700' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {payments.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>
                                        No payment records found
                                    </td>
                                </tr>
                            ) : (
                                payments.map((p, i) => {
                                    const methodLower = (p.method || '').toLowerCase();
                                    const isCheque = methodLower === 'cheque' || methodLower === 'check';
                                    const isBounced = p.is_bounced == 1 || p.is_bounced === '1';

                                    return (
                                        <tr key={p.id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '0.65rem 1rem' }}>{i + 1}</td>
                                            <td style={{ padding: '0.65rem 1rem' }}>{formatDate(p.paid_on)}</td>
                                            <td style={{ padding: '0.65rem 1rem' }}>{p.payment_ref_no || '--'}</td>
                                            <td style={{ padding: '0.65rem 1rem', fontWeight: 700, color: '#059669' }}>${parseFloat(p.amount || 0).toFixed(2)}</td>
                                            <td style={{ padding: '0.65rem 1rem', fontWeight: 600, color: '#6b7280' }}>
                                                {p.original_amount !== undefined && p.original_amount !== null ? `$${parseFloat(p.original_amount).toFixed(2)}` : '--'}
                                            </td>
                                            <td style={{ padding: '0.65rem 1rem', textTransform: 'capitalize' }}>{p.method || '--'}</td>
                                            <td style={{ padding: '0.65rem 1rem', color: 'var(--etpos-text-muted, #6b7280)' }}>{p.note || '--'}</td>
                                            <td style={{ padding: '0.65rem 1rem' }}>
                                                {isBounced ? (
                                                    <button
                                                        disabled={actionLoading}
                                                        onClick={() => handleRevertCheckReturn(p.id)}
                                                        style={{
                                                            background: '#ef4444',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '0.3rem 0.65rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '0.3rem',
                                                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                                                            opacity: actionLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        <RotateCcw size={13} /> Revert
                                                    </button>
                                                ) : isCheque ? (
                                                    <button
                                                        disabled={actionLoading}
                                                        onClick={() => handleApplyCheckReturn(p.id)}
                                                        style={{
                                                            background: '#f59e0b',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '0.3rem 0.65rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '0.3rem',
                                                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                                                            opacity: actionLoading ? 0.7 : 1
                                                        }}
                                                    >
                                                        <CreditCard size={13} /> Check Return
                                                    </button>
                                                ) : (
                                                    '--'
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                </div>

                {/* Footer */}
                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: 'var(--etpos-card)', borderRadius: '0 0 4px 4px' }}>
                    <button onClick={() => {
                        invoiceGenerator.printInvoicePDF({
                            invoiceNumber: invoiceNo,
                            invoiceDate: formatDate(txDate),
                            customerName: supplierName,
                            items: (t.purchase_lines || []).map(line => ({
                                sku: line.product?.sku || '',
                                name: line.product?.name || ('Product #' + line.product_id),
                                quantity: parseFloat(line.quantity || 0),
                                unitPrice: parseFloat(line.purchase_price || 0),
                                total: parseFloat(line.quantity || 0) * parseFloat(line.purchase_price || 0),
                            })),
                            payments: payments.map(p => ({
                                method: p.method,
                                amount: parseFloat(p.amount || 0),
                                date: formatDate(p.paid_on),
                            })),
                            subtotal: parseFloat(t.total_before_tax || t.final_total || 0),
                            discount: parseFloat(t.discount_amount || 0),
                            tax: parseFloat(t.tax_amount || 0),
                            total: finalTotal,
                            totalPaid: totalPaid,
                            amountDue: parseFloat(paymentDue || 0),
                            notes: t.additional_notes || '',
                        });
                    }} style={{ background: '#4f46e5', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', minWidth: '80px', justifyContent: 'center' }}>
                        <Printer size={16} /> Print
                    </button>
                    <button onClick={onClose} style={{ background: '#374151', color: 'var(--etpos-card)', border: 'none', borderRadius: '4px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Close
                    </button>
                </div>

            </div>

            {/* Add Payment Sub-Modal */}
            {showAddPaymentModal && (
                <AddPaymentModal
                    sale={t}
                    onClose={() => setShowAddPaymentModal(false)}
                    onSuccess={handlePaymentAdded}
                />
            )}
        </div>
    );
};

export default ViewPaymentsModal;

